import React from 'react';
import IconButton from './IconButton';
import { IconName } from './Icon';

const IconButtonDemo: React.FC = () => {
  const [activeIcon, setActiveIcon] = React.useState<IconName>('home');

  const icons: IconName[] = [
    'home', 'dashboard-chart', 'database', 'feed', 'water', 'biomas',
    'tasks', 'user', 'bell', 'settings', 'call', 'chat'
  ];

  const getIconColor = (iconName: IconName): string => {
    const colorMap: Record<IconName, string> = {
      'home': '#f59e0b',        // Amber for home
      'dashboard-chart': '#3b82f6', // Blue for analytics
      'database': '#6b7280',    // Gray for database
      'feed': '#f97316',        // Orange for feed
      'water': '#06b6d4',       // Cyan for water
      'biomas': '#10b981',      // Emerald for biomas
      'tasks': '#22c55e',       // Green for tasks/completed
      'user': '#8b5cf6',        // Purple for user
      'bell': '#f59e0b',        // Amber for notifications
      'settings': '#6b7280',    // Gray for settings
      'call': '#22c55e',        // Green for call
      'chat': '#3b82f6'         // Blue for chat
    };
    return colorMap[iconName] || '#6b7280';
  };

  const handleIconClick = (icon: IconName) => {
    setActiveIcon(icon);
    console.log(`Clicked ${icon} button`);
  };

  return (
    <div style={{ padding: '32px', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937', marginBottom: '16px' }}>
          IconButton Component Demo
        </h2>
        <p style={{ color: '#6b7280', marginBottom: '24px' }}>
          Interactive showcase of our custom IconButton component with all available icons
        </p>
        <div style={{ background: '#fef3c7', padding: '8px', marginBottom: '16px', borderRadius: '4px', fontSize: '14px' }}>
          Debug: Component loaded at {new Date().toLocaleTimeString()}
        </div>
      </div>

      {/* Icon Showcase with Colors */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
          Icon Showcase - Real Colors & Shapes
        </h3>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', 
          gap: '16px',
          padding: '16px',
          background: '#f9fafb',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          {icons.map((iconName) => (
            <div key={iconName} style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '12px',
              background: 'white',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
            }}>
              <IconButton
                icon={iconName}
                size="lg"
                variant="ghost"
                onClick={() => handleIconClick(iconName)}
                style={{
                  color: getIconColor(iconName),
                  marginBottom: '8px'
                }}
              />
              <span style={{ 
                fontSize: '12px', 
                color: '#6b7280', 
                textAlign: 'center',
                fontWeight: '500'
              }}>
                {iconName}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Size Variants */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
          Size Variants
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <IconButton icon="home" size="sm" variant="primary" tooltip="Small" />
          <IconButton icon="home" size="md" variant="primary" tooltip="Medium" />
          <IconButton icon="home" size="lg" variant="primary" tooltip="Large" />
          <IconButton icon="home" size="xl" variant="primary" tooltip="Extra Large" />
        </div>
      </div>

      {/* Style Variants */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
          Style Variants
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <IconButton icon="user" variant="primary" tooltip="Primary" />
          <IconButton icon="user" variant="secondary" tooltip="Secondary" />
          <IconButton icon="user" variant="ghost" tooltip="Ghost" />
          <IconButton icon="user" variant="danger" tooltip="Danger" />
        </div>
      </div>

      {/* Button States */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
          Button States
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <IconButton icon="bell" variant="secondary" tooltip="Normal" />
          <IconButton icon="bell" variant="secondary" isActive tooltip="Active" />
          <IconButton icon="bell" variant="secondary" disabled tooltip="Disabled" />
        </div>
      </div>

      {/* All Icons Grid */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
          All Available Icons
        </h3>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', 
          gap: '16px', 
          padding: '16px', 
          background: '#f9fafb', 
          borderRadius: '8px' 
        }}>
          {icons.map((icon) => (
            <div key={icon} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
              <IconButton
                icon={icon}
                variant="ghost"
                size="lg"
                isActive={activeIcon === icon}
                onClick={() => handleIconClick(icon)}
                tooltip={icon}
              />
              <span style={{ fontSize: '12px', color: '#6b7280', textAlign: 'center' }}>{icon}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
          Action Buttons
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <IconButton
            icon="call"
            variant="primary"
            onClick={() => alert('Calling...')}
            tooltip="Make a call"
          />
          <IconButton
            icon="chat"
            variant="secondary"
            onClick={() => alert('Opening chat...')}
            tooltip="Open chat"
          />
          <IconButton
            icon="settings"
            variant="ghost"
            onClick={() => alert('Opening settings...')}
            tooltip="Settings"
          />
        </div>
      </div>
    </div>
  );
};

export default IconButtonDemo;